import { CustomUseInfiniteQueryOptions } from '../types';
import { getNextPageParam, getPreviousPageParam } from '../utils';
import { hasNonEmptyProp } from '@/libs/common/utils';
import { ApiInstance, UserTrades, UserTradesParams } from '@/libs/sdk/api';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

export const useUserTrades = (
  api: ApiInstance,
  searchParams: UserTradesParams,
  options?: CustomUseInfiniteQueryOptions<UserTrades>
) => {
  const { page = 1, pageSize = 10 } = searchParams || {};
  const { queryKey, enabled = true, ...rest } = options || {};
  const query = useInfiniteQuery({
    ...rest,
    enabled: enabled && hasNonEmptyProp(searchParams, ['uid']),
    queryKey: [queryKey, 'UserTrades', searchParams],
    initialPageParam: { page, pageSize },
    queryFn: async ({ pageParam }) => {
      const { data } = await api.market.getUserTrades({ ...searchParams!, ...pageParam });
      return { ...data, ...pageParam };
    },
    placeholderData: keepPreviousData,
    getPreviousPageParam,
    getNextPageParam,
  });
  useEffect(() => {
    if (!query.isEnabled) return;
    const reftch = () => query.refetch({ cancelRefetch: false });
    api.sse.addEventListener('user:position_changed', reftch);
    api.sse.addEventListener('base:connected', reftch);
    return () => {
      api.sse.removeEventListener('user:position_changed', reftch);
      api.sse.removeEventListener('base:connected', reftch);
    };
  }, [query.isEnabled, api.sse]);
  return query;
};
